import React from 'react';
import {Box, Typography} from '@mui/material';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {PlanRow} from '../CreateBuilder/BuilderSourceDestination/PlanRow';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanSelector} from '../CreateBuilder/BuilderSourceDestination/PlanSelector';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  getSummary,
  getSummaryParts,
} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {ParentPlanRow} from '../CreateBuilder/BuilderSourceDestination/ParentPlanRow';
import {WithdrawalDestinationSubplan} from './WithdrawalDestinationSubplan';
import {BuilderCurrentAccountDetails} from './BuilderCurrentAccountDetails';
import {BuilderWithdrawalSummaryValidation} from './BuilderWithdrawalSummaryValidation';
import {isBankAccount} from '../../../utils/potsFilter';

interface WithdrawalDestinationParentProps {
  pot: BuilderPot;
  withdrawDestinationId?: string;
  fixed?: boolean;
  sameReview?: boolean;
  nominatedAccountId?: string;
  handleChange: (pot: BuilderPot | BuilderPot_dentistChildren) => void;
}

export const WithdrawalDestinationParent: React.FC<WithdrawalDestinationParentProps> = ({
  pot,
  withdrawDestinationId,
  fixed,
  sameReview,
  nominatedAccountId,
  handleChange,
}) => {
  const isBankPot = isBankAccount(pot) || !!pot.productIsBank;
  const dentistChildren: BuilderPot_dentistChildren[] = pot.dentistChildren ?? [];
  const hasChildren = !isBankPot && dentistChildren.length > 0;
  const isParentSelected = withdrawDestinationId === pot.id;
  const childSelected =
    hasChildren && dentistChildren.some((c) => c.id === withdrawDestinationId);

  // Bank account or non-bank leaf — selectable at the parent level.
  // Non-bank parent with dentist children — parent acts as a non-selectable header.
  const formikKeyPrefix =
    !hasChildren && isParentSelected && !isBankPot
      ? 'withdraw.destination'
      : childSelected
      ? 'withdraw.destination'
      : undefined;
  const {bgClass} = usePlanValidationLevel(formikKeyPrefix);

  if (hasChildren) {
    const {label, currentValue} = getSummaryParts(pot);
    return (
      <ParentPlanRow
        pot={pot}
        isSelected={childSelected}
        sameReview={sameReview}
        validationLevel={bgClass}
        summaryChildren={
          <Box sx={{display: 'flex', alignItems: 'center', flex: 1}}>
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {label}
            </Typography>
            {currentValue && (
              <Typography
                variant="body2"
                sx={{fontWeight: 600, ml: 'auto', whiteSpace: 'nowrap'}}
              >
                {currentValue}
              </Typography>
            )}
          </Box>
        }
        summaryTrailing={
          childSelected && formikKeyPrefix ? (
            <PlanValidationIcon formikKeyPrefix={formikKeyPrefix} />
          ) : undefined
        }
      >
        {dentistChildren.map((child) => (
          <WithdrawalDestinationSubplan
            key={child.id}
            subPlan={child}
            isSelected={withdrawDestinationId === child.id}
            handleChange={() => handleChange(child)}
          />
        ))}
      </ParentPlanRow>
    );
  }

  // Bank account or non-bank leaf — selectable directly
  const linked = nominatedAccountId === pot.id;
  const validatedBank = pot.isValidated ?? false;

  return (
    <PlanRow
      id={pot.id}
      reccId={pot.originalOvrId ?? null}
      curoId={pot.curoId}
      isSelected={isParentSelected}
      isSubplan={false}
      sameReview={sameReview}
    >
      <PlanAccordionShell
        expanded={isParentSelected}
        isSelected={isParentSelected}
        isSoleParent
        validationLevel={bgClass}
        sameReview={sameReview}
        summaryChildren={
          <>
            <PlanSelector
              mode="single"
              id={pot.id}
              isSelected={isParentSelected}
              fixed={fixed}
              handleChange={() => handleChange(pot)}
            />
            <Box display="flex" flexDirection="row">
              {getSummary(pot)}
            </Box>
          </>
        }
        summaryTrailing={
          <>
            {isBankPot && (
              <BuilderWithdrawalSummaryValidation
                needsLink
                linked={linked}
                validated={validatedBank}
              />
            )}
            {formikKeyPrefix && <PlanValidationIcon formikKeyPrefix={formikKeyPrefix} />}
          </>
        }
      >
        <BuilderCurrentAccountDetails currentAccount={pot} />
      </PlanAccordionShell>
    </PlanRow>
  );
};
